import { useState } from "react";
import axios from "axios";
import { format } from "date-fns";
import { toast } from "sonner";
import { Loader2, ArrowUpRight, CalendarDays } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Calendar } from "../components/ui/calendar";
import { cn } from "../lib/utils";
import { REQUIREMENT_OPTIONS } from "../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full border border-gold/20 bg-surface px-4 py-3.5 text-sm text-foreground placeholder:text-muted-foreground/80 outline-none transition-colors duration-300 focus:border-gold";

const labelCls = "mb-2 block text-[10px] font-semibold uppercase tracking-[0.24em] text-gold";

function Field({ label, name, type = "text", required = true, placeholder, value, onChange, testId, ...rest }) {
  return (
    <div>
      <label htmlFor={name} className={labelCls}>{label}{required && " *"}</label>
      <input
        id={name} name={name} type={type} required={required} placeholder={placeholder}
        value={value} onChange={onChange} className={inputCls} data-testid={testId}
        {...rest}
      />
    </div>
  );
}

function DateField({ label, value, onChange, testId }) {
  const [open, setOpen] = useState(false);
  const selected = value ? new Date(`${value}T00:00:00`) : undefined;
  return (
    <div>
      <label className={labelCls}>{label}</label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            data-testid={testId}
            className={cn(inputCls, "flex items-center justify-between gap-3 text-left", !value && "text-muted-foreground/80")}
          >
            <span>{selected ? format(selected, "dd/MM/yyyy") : "Pick a date (optional)"}</span>
            <CalendarDays className="h-4 w-4 shrink-0 text-gold" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-auto border-gold/20 bg-ink p-0" data-testid={`${testId}-popover`}>
          <Calendar
            mode="single"
            selected={selected}
            onSelect={(d) => {
              onChange(d ? format(d, "yyyy-MM-dd") : "");
              setOpen(false);
            }}
            initialFocus
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}

const EMPTY = {
  full_name: "", company_name: "", phone: "", email: "",
  pickup_location: "", delivery_location: "", cargo_type: "",
  approximate_load: "", required_date: "", requirement: "", additional_info: "",
};

export default function EnquiryForm({ compact = false, testIdPrefix = "quote-form" }) {
  const [searchParams] = useSearchParams();
  const preselect = searchParams.get("requirement");
  const [form, setForm] = useState(() => ({
    ...EMPTY,
    requirement: REQUIREMENT_OPTIONS.includes(preselect) ? preselect : "",
  }));
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, {
        ...form,
        pickup_location: form.pickup_location || "To be discussed",
        delivery_location: form.delivery_location || "To be discussed",
        cargo_type: form.cargo_type || form.requirement,
        requirement: form.requirement || form.cargo_type,
      });
      toast.success("Thank you. Our team will call or WhatsApp you shortly. For urgent loads call +91 97401 73666.");
      setForm(EMPTY);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => (d?.msg || "").replace(/^Value error, /, "")).filter(Boolean).join(" ")
        : typeof detail === "string" ? detail : "";
      toast.error(msg ? `Could not submit: ${msg}` : "Could not submit your enquiry. Please try again or call us directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-6" data-testid={testIdPrefix}>
      {!compact && (
        <p className="border-b border-gold/15 pb-3 text-[10px] font-bold uppercase tracking-[0.28em] text-gold">Your Details</p>
      )}
      <div className="grid gap-6 sm:grid-cols-2">
        <Field label="Full Name" name="full_name" placeholder="Your full name" value={form.full_name} onChange={set("full_name")} testId={`${testIdPrefix}-full-name`} />
        <Field label="Company Name" name="company_name" required={false} placeholder="Company (optional)" value={form.company_name} onChange={set("company_name")} testId={`${testIdPrefix}-company`} />
        <Field label="Phone Number" name="phone" type="tel" placeholder="+91" value={form.phone} onChange={set("phone")} inputMode="tel" pattern="[0-9+\-\s]{8,16}" title="Enter a valid phone number (8 to 15 digits)" testId={`${testIdPrefix}-phone`} />
        <Field label="Email" name="email" type="email" placeholder="you@company.com" value={form.email} onChange={set("email")} testId={`${testIdPrefix}-email`} />
      </div>

      {!compact && (
        <>
          <p className="border-b border-gold/15 pb-3 pt-4 text-[10px] font-bold uppercase tracking-[0.28em] text-gold">Route &amp; Cargo</p>
          <div className="grid gap-6 sm:grid-cols-2">
            <Field label="Pickup Location" name="pickup_location" placeholder="City / area of pickup" value={form.pickup_location} onChange={set("pickup_location")} testId={`${testIdPrefix}-pickup`} />
            <Field label="Delivery Location" name="delivery_location" placeholder="City / area of delivery" value={form.delivery_location} onChange={set("delivery_location")} testId={`${testIdPrefix}-delivery`} />
            <Field label="Cargo / Vehicle Type" name="cargo_type" placeholder="e.g. Machinery, bus, goods" value={form.cargo_type} onChange={set("cargo_type")} testId={`${testIdPrefix}-cargo-type`} />
            <Field label="Approximate Load" name="approximate_load" required={false} placeholder="e.g. 5 MT / 1 vehicle" value={form.approximate_load} onChange={set("approximate_load")} testId={`${testIdPrefix}-load`} />
          </div>
          <p className="border-b border-gold/15 pb-3 pt-4 text-[10px] font-bold uppercase tracking-[0.28em] text-gold">Schedule &amp; Requirement</p>
          <div className="grid gap-6 sm:grid-cols-2">
            <DateField label="Required Date" value={form.required_date} onChange={(v) => setForm((f) => ({ ...f, required_date: v }))} testId={`${testIdPrefix}-date`} />
            <div>
              <label htmlFor="requirement" className={labelCls}>Transportation Requirement *</label>
              <select
                id="requirement" name="requirement" required
                value={form.requirement} onChange={set("requirement")}
                className={inputCls} data-testid={`${testIdPrefix}-requirement`}
              >
                <option value="">Select a category</option>
                {REQUIREMENT_OPTIONS.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </div>
          </div>
        </>
      )}

      {compact && (
        <div>
          <label htmlFor="requirement" className={labelCls}>Transportation Requirement *</label>
          <select
            id="requirement" name="requirement" required
            value={form.requirement} onChange={set("requirement")}
            className={inputCls} data-testid={`${testIdPrefix}-requirement`}
          >
            <option value="">Select a category</option>
            {REQUIREMENT_OPTIONS.map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
        </div>
      )}

      <div>
        <label htmlFor="additional_info" className={labelCls}>Additional Information</label>
        <textarea
          id="additional_info" name="additional_info" rows={compact ? 3 : 4}
          placeholder="Tell us anything else about your requirement"
          value={form.additional_info} onChange={set("additional_info")}
          className={inputCls} data-testid={`${testIdPrefix}-additional-info`}
        />
      </div>

      <button type="submit" disabled={sending} className="btn-solid w-full text-center max-sm:tracking-[0.1em] sm:w-auto" data-testid={`${testIdPrefix}-submit`}>
        {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowUpRight className="h-4 w-4" />}
        {sending ? "Submitting…" : "Submit Transportation Enquiry"}
      </button>
    </form>
  );
}
